import { useState } from "react";

import { IconButton } from "@similarweb/ui-components";

import type { ICountryObject } from "app/services/CountryService";

import { useCustomIndustryAgent } from "../../CustomHooks/useCustomIndustryAgent";
import { CancelRunModal } from "./CancelRunModal";
import { RunningComposer } from "./RunningComposer";
import { RunProgressCard } from "./RunProgressCard";
import { hasPublishedIndustry } from "./stepPlan";
import { MinimizeRow, RunColumn, RunLayout, RunProgressPanel, StepWrapper } from "./styledComponents";
import { translateWithFallback as t } from "./translateWithFallback";

type GeneratingStepProps = {
    selectedCountry: ICountryObject;
    onCancel: () => void;
    onClose: () => void;
};

export const GeneratingStep = ({ selectedCountry, onCancel, onClose }: GeneratingStepProps) => {
    const { state, description, completedSteps } = useCustomIndustryAgent();
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

    return (
        <StepWrapper data-automation="market-builder-v2-generating-step">
            <RunLayout data-automation="market-builder-v2-generating-card">
                <RunColumn>
                    <RunningComposer
                        description={description}
                        selectedCountry={selectedCountry}
                        // A finished run has nothing left to cancel, and this view can still be
                        // sitting on `done` when opening the industry failed.
                        canStop={state !== "done" && !hasPublishedIndustry(completedSteps)}
                        onStop={() => setIsCancelModalOpen(true)}
                    />
                </RunColumn>
                <RunProgressPanel data-automation="market-builder-v2-run-progress-card">
                    <RunProgressCard />
                </RunProgressPanel>
            </RunLayout>
            <MinimizeRow>
                <IconButton
                    type="primary"
                    iconName="collapsed"
                    iconSize="xs"
                    data-automation="market-builder-v2-close-btn"
                    onClick={onClose}
                >
                    {t("market.builder.v2.progress.minimize", "Minimize this view")}
                </IconButton>
            </MinimizeRow>
            <CancelRunModal
                isOpen={isCancelModalOpen}
                onStay={() => setIsCancelModalOpen(false)}
                onCancelRun={() => {
                    setIsCancelModalOpen(false);
                    onCancel();
                }}
            />
        </StepWrapper>
    );
};
